// ────────────────────────────────────────────────────────────────────────────
// Which of a patient's claims belong on the statement this entity is printing.
//
// Normally: the ones still carrying a balance. A fully-paid claim is not billed
// again, and listing it would only pad the sheet.
//
// The exception is a patient the facility owes money to. When nothing is
// outstanding but the entity is holding unapplied credit, the statement is the
// only thing that tells the patient about it — "your account holds a credit of
// $300, contact our billing office". Suppressing it because the balance is zero
// meant the one patient who most needed a letter was the one who could not be
// sent one. Those sheets print with Balance Due $0.00 and the credit line; see
// generateStatement's totals block, which shows credit and never deducts it.
//
// Both figures are per claim, matching the backend's split in
// services/patientCredit.service.js: outstanding = max(0, responsibility − paid),
// credit = max(0, paid − responsibility). At most one is non-zero on a claim.
// ────────────────────────────────────────────────────────────────────────────

export const claimOutstanding = (c) =>
  Math.max(0, (parseFloat(c.patient_responsibility) || 0) - (parseFloat(c.patient_paid) || 0));

export const claimCredit = (c) =>
  Math.max(0, (parseFloat(c.patient_paid) || 0) - (parseFloat(c.patient_responsibility) || 0));

/**
 * Pick the claims for one patient's statement from a statement-data payload
 * (`{ patient, facility, claims, credit_summary }`). Returns [] when there is
 * nothing to print — no balance and no credit — so the caller can skip them.
 *
 * The payload is already scoped to the printing facility, so `credit_summary`
 * here is that entity's credit, not the patient's account-wide total. An imaging
 * statement must not advertise credit the hospital is holding.
 */
export function selectStatementClaims(d) {
  const all = d?.claims || [];
  const owed = all.filter((c) => claimOutstanding(c) > 0);
  if (owed.length > 0) return owed;

  const credit = parseFloat(d?.credit_summary?.unapplied_credit) || 0;
  if (credit > 0) return all.filter((c) => claimCredit(c) > 0);

  return [];
}
